import {
  Scene,
  Group,
  OrthographicCamera,
  WebGLRenderTarget,
  Vector2,
  Mesh,
  Color,
  PlaneGeometry,
  ShaderMaterial,
  MathUtils,
  Vector3,
} from 'three'
import type Common from './common'
import type AssetsCta from './assets'
import type AssetsHero from './assetsHero'

import {getScaleDelta} from './utils/math'
import circleVert from './shaders/circleVert'
import circleFrag from './shaders/circleFrag'

import starVert from './shaders/starVert'
import starFrag from './shaders/starFrag'
import utilGLSL from './shaders/utilGlsl'

export default class Stars {
  scene: Scene = new Scene()
  group: Group = new Group()
  camera: OrthographicCamera = new OrthographicCamera(1, 1, 1, 1, 1, 1)
  fbo: WebGLRenderTarget = new WebGLRenderTarget(1, 1)
  circles: Mesh[] = []
  resolution: Vector2 = new Vector2()
  min_radius: number = 0.22
  max_radius: number = 0.6
  alphaRange: Vector2 = new Vector2(1.0, 0.9)
  uTime: {value: number} = {value: 0}
  circleNum: number = 7
  bgColor: Color = new Color(0x000000)
  uWindowScale: {value: number} = {value: 1}
  minStarCircleIndex: number = 1
  commonUniforms: {[uniform: string]: {value: number | Vector2 | Color}} = {
    uTime: this.uTime,
    uMaxRadius: {
      value: this.max_radius,
    },
    uMinRadius: {
      value: this.min_radius,
    },
    uAlphaRange: {
      value: this.alphaRange,
    },
    uBgColor: {
      value: this.bgColor,
    },
    uWindowScale: this.uWindowScale,
  }
  lineWidth: number = 0.011
  starScale: number = 0.9
  speedProgress: {current: number; target: number} = {current: 0, target: 0}
  starOffsetScaleY: number = 0.08
  common?: Common
  assets?: AssetsCta | AssetsHero
  isHero: boolean = true
  constructor() {
    // this.fbo.samples = 16
    this.scene.add(this.group)
    this.camera.near = 0.01
    this.camera.far = 50

    this.camera.position.set(0, 0, 10)
  }
  init(
    common: Common | undefined,
    assets: AssetsCta | AssetsHero | undefined,
    starOffsetScaleY: number,
    isHero: boolean,
    bgColor: Color,
  ) {
    this.bgColor.copy(bgColor)
    this.isHero = isHero
    this.common = common
    this.assets = assets
    if (this.isHero) {
      this.max_radius = 0.65
      this.min_radius = 0.2
      this.alphaRange.set(1.0, 0.9)
      this.circleNum = 9
      this.lineWidth = 0.01
      this.starScale = 0.9
      this.minStarCircleIndex = 2
    }
    this.starOffsetScaleY = starOffsetScaleY

    for (let i = 0; i < this.circleNum; i++) {
      const angles: number[] = []
      const radius = ((i + 1) / this.circleNum) * (this.max_radius - this.min_radius) + this.min_radius
      const ANGLE_NUM = 2

      const uniforms = {
        uWidth: {
          value: this.lineWidth,
        },
        uRadius: {
          value: radius,
        },
        uColor1: {
          value: new Color(0x040639),
        },
        uColor2: {
          value: new Color(0x042c1a),
        },
        uShine1: {
          value: new Color(0x4792a0),
        },
        uShine2: {
          value: new Color(0x8e47fe),
        },
        uResolution: {
          value: this.resolution,
        },
        uAngles: {
          value: angles,
        },
        ...this.commonUniforms,
      }

      const defines = {
        ANGLE_NUM,
        TYPE: this.isHero ? 0 : 1,
        IS_STAR: i < this.minStarCircleIndex ? 0 : 1,
      }

      const mesh = new Mesh(
        new PlaneGeometry(1, 1, 1, 100),
        new ShaderMaterial({
          vertexShader: circleVert,
          fragmentShader: circleFrag,
          uniforms,
          defines,
          transparent: true,
        }),
      )

      mesh.userData.uniforms = uniforms
      mesh.userData.angles = angles
      mesh.userData.radius = radius
      mesh.name = 'circle'

      mesh.userData.speed = 0.00025 //MathUtils.randFloat(0.00025, 0.0005)

      let formerAngle: number = (i - 2) / (this.circleNum - 2) + (Math.random() - 0.5) * 0.05
      this.group.add(mesh)

      if (i < this.minStarCircleIndex) continue
      for (let j = 0; j < ANGLE_NUM; j++) {
        angles[j] = formerAngle = 0.5 + formerAngle
        const star = new Mesh(
          new PlaneGeometry(0.1 * this.starScale, 0.1 * this.starScale),
          new ShaderMaterial({
            vertexShader: starVert,
            fragmentShader: utilGLSL + starFrag,
            uniforms: {
              uMask: {
                value: this.assets?.images.starShape.texture,
              },
              uShadow: {
                value: this.assets?.images.starShapeShadow.texture,
              },
              uShadowGlow: {
                value: this.assets?.images.starShapeGlow.texture,
              },
              uColor: {
                value: new Color(0x8246f7),
              },
              uColor2: {
                value: new Color(0x55f2ea),
              },
              uRandom: {
                value: new Vector3(Math.random(), Math.random(), Math.random()),
              },
              uRadius: uniforms.uRadius,
              ...this.commonUniforms,
            },
            transparent: true,
          }),
        )

        star.name = 'star'

        mesh.add(star)

        star.position.z = 1
      }
    }

    this.resize()
  }

  resize() {
    if (this.common) {
      const width = this.common?.wrapperDimentions.x
      const height = this.common?.wrapperDimentions.y
      const maxWidth = 1800

      this.uWindowScale.value = Math.min(1, maxWidth / width)

      this.resolution.set(width, height)
      this.camera.left = -width / 2
      this.camera.right = width / 2
      this.camera.top = height / 2
      this.camera.bottom = -height / 2
      this.camera.updateProjectionMatrix()

      const scale = this.common?.isMobile ? 2 : 1

      this.group.scale.set(width * scale, width * scale, 1)
      this.group.position.y = height * this.starOffsetScaleY

      this.fbo.setSize(width, height)
    }
  }

  speedUp() {
    this.speedProgress.target = 1
  }

  speedDown() {
    this.speedProgress.target = 0
  }

  update() {
    if (this.commonUniforms.uMaxRadius) this.commonUniforms.uMaxRadius.value = this.max_radius
    if (this.commonUniforms.uMinRadius) this.commonUniforms.uMinRadius.value = this.min_radius

    if (this.common) {
      this.speedProgress.current +=
        (this.speedProgress.target - this.speedProgress.current) * getScaleDelta(2, this.common.delta)
    }
    this.group.traverse((circleMesh: unknown) => {
      if (circleMesh instanceof Mesh) {
        // eslint-disable-next-line eqeqeq
        if (circleMesh.name == 'circle') {
          circleMesh.userData.radius -= this.min_radius
          // circleMesh.userData.radius += 0.0002 * (1.0 + this.speedProgress.current * 5)
          circleMesh.userData.radius /= this.max_radius - this.min_radius
          circleMesh.userData.radius = circleMesh.userData.radius - Math.floor(circleMesh.userData.radius)
          circleMesh.userData.radius *= this.max_radius - this.min_radius
          circleMesh.userData.radius += this.min_radius
          circleMesh.userData.uniforms.uRadius.value = circleMesh.userData.radius
          const radius = circleMesh.userData.radius
          const angles = circleMesh.userData.angles
          const speed = circleMesh.userData.speed
          // eslint-disable-next-line github/array-foreach
          circleMesh.children.forEach((starMesh: unknown, index: number) => {
            if (starMesh instanceof Mesh) {
              // eslint-disable-next-line eqeqeq
              if (starMesh.name == 'star') {
                angles[index] -= speed * (1.0 + this.speedProgress.current * 5)
                starMesh.position.x = Math.cos(angles[index] * Math.PI * 2) * radius * this.uWindowScale.value
                starMesh.position.y = Math.sin(angles[index] * Math.PI * 2) * radius * this.uWindowScale.value
                starMesh.rotation.z = angles[index] * Math.PI * 2
              }
            }
          })
        }
      }
    })
    if (this.commonUniforms.uTime && this.common) {
      this.uTime.value += this.common.delta * MathUtils.lerp(1, 2, this.speedProgress.current)
    }

    this.common?.renderer.setClearColor(this.bgColor)

    this.common?.renderer.setRenderTarget(this.fbo)
    this.common?.renderer.render(this.scene, this.camera)
  }
}
